import { useState, useEffect, useCallback, useRef } from "react";
import {
  Upload, Mail, Loader2, FileSpreadsheet, Download, Trash2,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, Wallet, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { API, apiCall, formatDate } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Filter } from "lucide-react";

const MAX_EMAILS = 50000;

const StatusBadge = ({ status }) => {
  const map = {
    queued: { label: "En cola", cls: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30" },
    processing: { label: "Procesando", cls: "bg-blue-500/15 text-blue-500 border-blue-500/30" },
    completed: { label: "Completada", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
    error: { label: "Error", cls: "bg-rose-500/15 text-rose-500 border-rose-500/30" },
  };
  const m = map[status] || map.queued;
  return <Badge className={`${m.cls} text-[10px]`}>{m.label}</Badge>;
};

export const EmailBulkTab = ({ user }) => {
  const [screen, setScreen] = useState("list"); // list | new | detail
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [selected, setSelected] = useState(null); // full detail
  const [pollTick, setPollTick] = useState(0);
  const pollingRef = useRef(null);

  // New campaign form
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [useName, setUseName] = useState(false);
  const [starting, setStarting] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ ok: ok1, data: d1 }, { ok: ok2, data: d2 }] = await Promise.all([
        apiCall("GET", `${API}/email-bulk/list`),
        apiCall("GET", `${API}/my/balance`),
      ]);
      if (ok1) setCampaigns(d1.campaigns || []);
      if (ok2) setBalance(d2);
    } catch { toast.error("Error al cargar campanas"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto refresh every 3s if there's an active campaign
  useEffect(() => {
    const hasActive = campaigns.some(c => c.status === "queued" || c.status === "processing");
    if (!hasActive) return;
    pollingRef.current = setInterval(() => setPollTick(t => t + 1), 3000);
    return () => clearInterval(pollingRef.current);
  }, [campaigns]);

  useEffect(() => { if (pollTick > 0) loadAll(); }, [pollTick, loadAll]);

  // Load detail when screen=detail
  useEffect(() => {
    if (screen !== "detail" || !selected?.analysis_id) return;
    let stopped = false;
    const fetchDetail = async () => {
      const { ok, data } = await apiCall("GET", `${API}/email-bulk/${selected.analysis_id}`);
      if (ok && !stopped) setSelected(data);
    };
    fetchDetail();
    const interval = setInterval(() => {
      if (selected?.status === "processing" || selected?.status === "queued") fetchDetail();
    }, 3000);
    return () => { stopped = true; clearInterval(interval); };
  }, [screen, selected?.analysis_id, selected?.status]);

  const handleStart = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Seleccioná un archivo");
    setStarting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("campaign_name", name.trim() || file.name);
      fd.append("use_name", useName ? "true" : "false");
      const { ok, data } = await apiCall("POST", `${API}/email-bulk/start`, fd);
      if (ok) {
        toast.success(`Campaña iniciada. ${data.total} emails a verificar. Costo estimado: $${data.estimated_cost.toLocaleString("es-AR", { minimumFractionDigits: 2 })}`);
        if (data.rotated_old_campaigns > 0) {
          toast.info(`Se eliminaron ${data.rotated_old_campaigns} campañas antiguas para hacer espacio.`);
        }
        setFile(null); setName(""); setUseName(false);
        await loadAll();
        setScreen("list");
      } else {
        toast.error(data?.detail || "No se pudo iniciar");
      }
    } catch (e) {
      toast.error(`Error: ${e.message || e}`);
    } finally {
      setStarting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { ok, data } = await apiCall("DELETE", `${API}/email-bulk/${confirmDelete.analysis_id}`);
      if (ok) {
        toast.success("Campaña eliminada");
        setConfirmDelete(null);
        await loadAll();
      } else {
        toast.error(data?.detail || "No se pudo eliminar");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = async (analysisId, campName, opts = {}) => {
    const t = toast.loading("Preparando descarga...");
    try {
      const qs = new URLSearchParams();
      if (opts.minScore) qs.set("min_score", String(opts.minScore));
      if (opts.onlyValid) qs.set("only_valid", "true");
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const url = `${API}/email-bulk/${analysisId}/download${suffix}`;
      console.log("[DOWNLOAD] Fetching", url);
      const res = await fetch(url, { credentials: "include" });
      console.log("[DOWNLOAD] Response", res.status, res.headers.get("content-type"));
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.dismiss(t);
        return toast.error(j?.detail || `Error ${res.status} al descargar`);
      }
      const blob = await res.blob();
      console.log("[DOWNLOAD] Blob size", blob.size, "type", blob.type);
      if (blob.size === 0) {
        toast.dismiss(t);
        return toast.error("El servidor devolvio un archivo vacio");
      }
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      const suffixName =
        (opts.minScore ? `_match${Math.round(opts.minScore * 100)}` : "") +
        (opts.onlyValid ? "_validos" : "");
      const safeName = String(campName || "campana").replace(/[^\w\-]+/g, "_").slice(0, 40);
      a.download = `veritel_email_${safeName}${suffixName}.xlsx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Slight delay before removing so the browser has time to start the download
      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(objUrl);
      }, 500);
      toast.dismiss(t);
      toast.success(`Descarga lista: ${a.download}`);
    } catch (e) {
      console.error("[DOWNLOAD] Error", e);
      toast.dismiss(t);
      toast.error("Error al descargar: " + (e?.message || e));
    }
  };

  // ============================================================
  // NEW CAMPAIGN SCREEN
  // ============================================================
  if (screen === "new") {
    const emailPrice = balance?.email_price || 1;
    const availableBalance = balance?.balance_ars || 0;
    const maxByBalance = emailPrice > 0 ? Math.floor(availableBalance / emailPrice) : 0;
    return (
      <div className="space-y-6" data-testid="email-bulk-new-screen">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: "var(--app-text)" }}>Nueva verificación masiva de emails</h2>
            <p className="text-sm" style={{ color: "var(--app-text-secondary)" }}>Subí un CSV/XLSX/TXT con emails y descargá el Excel con los resultados.</p>
          </div>
          <Button variant="ghost" onClick={() => setScreen("list")} style={{ color: "var(--app-text-secondary)" }}>Cancelar</Button>
        </div>

        <div className="p-5 rounded-lg" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
          <div className="flex items-center gap-4 flex-wrap text-sm">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-amber-500" />
              <span style={{ color: "var(--app-text-secondary)" }}>Saldo actual:</span>
              <span className="font-bold text-amber-500">${availableBalance.toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-violet-500" />
              <span style={{ color: "var(--app-text-secondary)" }}>Precio por email:</span>
              <span className="font-semibold" style={{ color: "var(--app-text)" }}>${emailPrice.toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: "var(--app-text-secondary)" }}>Máx. con tu saldo:</span>
              <span className="font-semibold" style={{ color: "var(--app-text)" }}>{maxByBalance.toLocaleString("es-AR")} emails</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleStart} className="p-6 rounded-lg space-y-5" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }} data-testid="email-bulk-form">
          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--app-text)" }}>Nombre de la campaña (opcional)</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ej: Leads Enero 2026" data-testid="new-email-campaign-name" />
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--app-text)" }}>Tipo de análisis</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setUseName(false)}
                className={`p-4 rounded-lg text-left transition ${!useName ? "ring-2 ring-violet-500" : ""}`}
                style={{ backgroundColor: "var(--app-input-bg)", border: "1px solid var(--app-border)" }}
                data-testid="mode-email-only"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Mail className={`w-4 h-4 ${!useName ? "text-violet-500" : ""}`} style={!useName ? {} : { color: "var(--app-text-muted)" }} />
                  <span className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>Solo email</span>
                  {!useName && <Badge className="bg-violet-500/15 text-violet-500 border-violet-500/30 text-[10px] ml-auto">Seleccionado</Badge>}
                </div>
                <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>
                  Sintaxis, dominio, MX, SMTP, descartable, rol/genérico.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setUseName(true)}
                className={`p-4 rounded-lg text-left transition ${useName ? "ring-2 ring-violet-500" : ""}`}
                style={{ backgroundColor: "var(--app-input-bg)", border: "1px solid var(--app-border)" }}
                data-testid="mode-email-plus-name"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileSpreadsheet className={`w-4 h-4 ${useName ? "text-violet-500" : ""}`} style={useName ? {} : { color: "var(--app-text-muted)" }} />
                  <span className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>Email + Nombre asociado</span>
                  {useName && <Badge className="bg-violet-500/15 text-violet-500 border-violet-500/30 text-[10px] ml-auto">Seleccionado</Badge>}
                </div>
                <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>
                  Todo lo anterior + coincidencia entre el email y el nombre del contacto (detecta impostores).
                </p>
              </button>
            </div>
            {useName && (
              <div className="mt-2 p-3 rounded-lg text-xs" style={{ backgroundColor: "var(--app-input-bg)", border: "1px solid var(--app-border)", color: "var(--app-text-muted)" }}>
                <strong style={{ color: "var(--app-text-secondary)" }}>Requerido:</strong> tu archivo debe tener una columna con el nombre del contacto
                (encabezado con &quot;nombre&quot;, &quot;name&quot;, &quot;cliente&quot;, &quot;contacto&quot;, &quot;titular&quot; o &quot;apellido&quot;).
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--app-text)" }}>Archivo con emails *</label>
            <div
              className="upload-zone rounded-lg p-8 text-center cursor-pointer transition"
              onClick={() => document.getElementById("email-file-input").click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("drag-over"); }}
              onDragLeave={e => e.currentTarget.classList.remove("drag-over")}
              onDrop={e => {
                e.preventDefault(); e.currentTarget.classList.remove("drag-over");
                if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
              }}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileSpreadsheet className="w-6 h-6 text-emerald-500" />
                  <div>
                    <p className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>{file.name}</p>
                    <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-rose-500">Cambiar</Button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-violet-500" />
                  <p className="font-medium" style={{ color: "var(--app-text)" }}>Arrastrá o hacé click para subir</p>
                  <p className="text-xs mt-1" style={{ color: "var(--app-text-muted)" }}>
                    CSV · TXT · XLSX · XLS — hasta 20 MB · máx {MAX_EMAILS.toLocaleString("es-AR")} emails
                  </p>
                </>
              )}
              <input
                id="email-file-input"
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="email-file-input"
              />
            </div>
          </div>

          <div className="p-4 rounded-lg text-xs" style={{ backgroundColor: "var(--app-input-bg)", border: "1px solid var(--app-border)", color: "var(--app-text-muted)" }}>
            <p className="font-semibold mb-1" style={{ color: "var(--app-text-secondary)" }}>💡 Consejos</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Debe tener una columna con encabezado que contenga &quot;email&quot;, &quot;mail&quot; o &quot;correo&quot;.</li>
              <li>Se detectan y omiten duplicados automáticamente.</li>
              <li>El costo se descuenta al finalizar (solo por emails procesados con sintaxis válida).</li>
              <li>Los archivos con {'>'} 1000 emails pueden tardar varios minutos.</li>
            </ul>
          </div>

          <Button type="submit" disabled={starting || !file} className="w-full bg-violet-600 hover:bg-violet-700 text-white h-11" data-testid="start-email-campaign-btn">
            {starting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
            Iniciar verificación
          </Button>
        </form>
      </div>
    );
  }

  // ============================================================
  // DETAIL SCREEN
  // ============================================================
  if (screen === "detail" && selected) {
    const preview = selected.results_preview || [];
    return (
      <div className="space-y-6" data-testid="email-bulk-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: "var(--app-text)" }}>{selected.campaign_name}</h2>
            <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{selected.filename} · {formatDate(selected.created_at)}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setScreen("list")} style={{ color: "var(--app-text-secondary)" }}>Volver</Button>
            {selected.status === "completed" && (
              <>
                <Button onClick={() => handleDownload(selected.analysis_id, selected.campaign_name)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="download-detail-btn">
                  <Download className="w-4 h-4 mr-2" />Descargar Excel
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="filter-download-btn" style={{ color: "var(--app-text-secondary)" }}>
                      <Filter className="w-4 h-4 mr-2" />Descargas filtradas
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel>Descargas rapidas</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleDownload(selected.analysis_id, selected.campaign_name, { onlyValid: true })} data-testid="dl-only-valid">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" />Solo validos
                    </DropdownMenuItem>
                    {selected.use_name && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs" style={{ color: "var(--app-text-muted)" }}>Filtros por coincidencia de nombre</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleDownload(selected.analysis_id, selected.campaign_name, { minScore: 0.7 })} data-testid="dl-match-70">
                          <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 text-[10px] mr-2">≥ 70%</Badge>
                          Match alto (recomendado)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(selected.analysis_id, selected.campaign_name, { minScore: 0.5 })} data-testid="dl-match-50">
                          <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30 text-[10px] mr-2">≥ 50%</Badge>
                          Match medio o mejor
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(selected.analysis_id, selected.campaign_name, { minScore: 0.9 })} data-testid="dl-match-90">
                          <Badge className="bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30 text-[10px] mr-2">≥ 90%</Badge>
                          Match altisimo
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownload(selected.analysis_id, selected.campaign_name, { minScore: 0.7, onlyValid: true })} data-testid="dl-match-70-valid">
                          <Badge className="bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 text-[10px] mr-2">Combo</Badge>
                          Solo válidos + Match ≥ 70%
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Total", selected.total, "text-violet-500"],
            ["Válidos", selected.valid_count, "text-emerald-500"],
            ["Inválidos", selected.invalid_count, "text-rose-500"],
            ["Descartables", selected.disposable_count, "text-amber-500"],
          ].map(([label, val, cls]) => (
            <div key={label} className="p-4 rounded-lg" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
              <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{label}</p>
              <p className={`text-2xl font-bold ${cls}`}>{(val || 0).toLocaleString("es-AR")}</p>
            </div>
          ))}
        </div>

        {(selected.status === "processing" || selected.status === "queued") && (
          <div className="p-4 rounded-lg" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
            <div className="flex items-center justify-between text-sm mb-2">
              <span style={{ color: "var(--app-text)" }}>{selected.processed || 0} / {selected.total || 0} procesados</span>
              <span className="font-semibold text-violet-500">{selected.progress || 0}%</span>
            </div>
            <Progress value={selected.progress || 0} className="h-2" />
          </div>
        )}

        {selected.total_cost !== undefined && selected.status === "completed" && (
          <div className="p-4 rounded-lg flex items-center gap-3" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
            <Wallet className="w-5 h-5 text-amber-500" />
            <p className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
              Costo descontado: <span className="font-bold text-amber-500">${(selected.total_cost || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
              <span className="text-xs ml-2" style={{ color: "var(--app-text-muted)" }}>({selected.processed} emails × ${selected.email_price})</span>
            </p>
          </div>
        )}

        {preview.length > 0 && (
          <div className="rounded-lg overflow-hidden" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: "1px solid var(--app-border)" }}>
              <h3 className="font-semibold text-sm" style={{ color: "var(--app-text)" }}>Vista previa (primeros 20)</h3>
              {selected.status === "completed" && (
                <span className="text-xs" style={{ color: "var(--app-text-muted)" }}>Descargá el Excel para ver todos</span>
              )}
            </div>
            <div className="divide-y overflow-x-auto" style={{ borderColor: "var(--app-border)" }}>
              {preview.map((r, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-sm" data-testid={`preview-row-${i}`}>
                  {r.is_valid
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    : <XCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-mono truncate" style={{ color: "var(--app-text)" }}>{r.email}</div>
                    {r.name && (
                      <div className="text-xs truncate" style={{ color: "var(--app-text-muted)" }}>
                        {r.name}
                        {typeof r.name_score === "number" && (
                          <span className={`ml-2 font-semibold ${r.name_match ? "text-emerald-500" : "text-amber-500"}`}>
                            {r.name_match ? `✓ ${Math.round(r.name_score)}%` : `${Math.round(r.name_score)}%`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {r.is_disposable && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-[10px]">Desechable</Badge>}
                    {r.is_role_account && <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/30 text-[10px]">Rol/Genérico</Badge>}
                    {!r.domain_exists && <Badge className="bg-rose-500/10 text-rose-500 border-rose-500/30 text-[10px]">Dominio no existe</Badge>}
                    {!r.mx_records && r.domain_exists && <Badge className="bg-rose-500/10 text-rose-500 border-rose-500/30 text-[10px]">Sin MX</Badge>}
                    {r.smtp_verified === "catch_all" && <Badge className="bg-zinc-500/10 text-zinc-500 border-zinc-500/30 text-[10px]">Catch-all</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================================
  // LIST SCREEN
  // ============================================================
  return (
    <div className="space-y-6" data-testid="email-bulk-list">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: "var(--app-text)" }}>Verificación masiva de Emails</h2>
          <p className="text-sm" style={{ color: "var(--app-text-secondary)" }}>Subí un archivo con emails y obtené un Excel con los resultados de verificación.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadAll} style={{ color: "var(--app-text-secondary)" }} data-testid="refresh-email-list">
            <RefreshCw className="w-4 h-4 mr-2" />Actualizar
          </Button>
          <Button onClick={() => setScreen("new")} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="new-email-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />Nueva verificación
          </Button>
        </div>
      </div>

      {balance && (
        <div className="p-4 rounded-lg flex items-center gap-3 flex-wrap" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }}>
          <Wallet className="w-5 h-5 text-amber-500" />
          <span className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
            Saldo: <span className="font-bold text-amber-500">${(balance.balance_ars || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
          </span>
          <span className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
            · Precio por email: <span className="font-semibold" style={{ color: "var(--app-text)" }}>${(balance.email_price || 1).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
          </span>
          <span className="text-sm" style={{ color: "var(--app-text-secondary)" }}>
            · Podés verificar hasta <span className="font-semibold" style={{ color: "var(--app-text)" }}>{(balance.estimated_email || 0).toLocaleString("es-AR")}</span> emails más
          </span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16" style={{ color: "var(--app-text-muted)" }}>
          <Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando...
        </div>
      ) : campaigns.length === 0 ? (
        <div className="p-12 rounded-lg text-center" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }} data-testid="empty-state">
          <Mail className="w-12 h-12 mx-auto mb-3 text-violet-500 opacity-70" />
          <p className="font-semibold mb-1" style={{ color: "var(--app-text)" }}>Todavía no verificaste emails</p>
          <p className="text-sm mb-4" style={{ color: "var(--app-text-muted)" }}>Empezá subiendo un archivo CSV, TXT o Excel con emails.</p>
          <Button onClick={() => setScreen("new")} className="bg-violet-600 hover:bg-violet-700 text-white">
            <Plus className="w-4 h-4 mr-2" />Crear primera verificación
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <div key={c.analysis_id} className="p-4 rounded-lg flex items-center gap-4" style={{ backgroundColor: "var(--app-card)", border: "1px solid var(--app-border)" }} data-testid={`campaign-row-${c.analysis_id}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-violet-500/10 flex-shrink-0">
                <Mail className="w-5 h-5 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold truncate" style={{ color: "var(--app-text)" }}>{c.campaign_name}</p>
                  <StatusBadge status={c.status} />
                  {c.use_name && (
                    <Badge className="bg-violet-500/10 text-violet-500 border-violet-500/30 text-[10px]">+Nombre</Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs mt-1" style={{ color: "var(--app-text-muted)" }}>
                  <span>{formatDate(c.created_at)}</span>
                  <span>{(c.total || 0).toLocaleString("es-AR")} emails</span>
                  {c.status === "completed" && (
                    <>
                      <span className="text-emerald-500">✓ {c.valid_count || 0} válidos</span>
                      <span className="text-rose-500">✗ {c.invalid_count || 0} inválidos</span>
                      <span className="text-amber-500">${(c.total_cost || 0).toLocaleString("es-AR", { minimumFractionDigits: 2 })}</span>
                    </>
                  )}
                  {(c.status === "processing" || c.status === "queued") && (
                    <span>{c.processed || 0} / {c.total || 0} · {c.progress || 0}%</span>
                  )}
                  {c.status === "error" && c.error && (
                    <span className="text-rose-500 truncate flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />{c.error}
                    </span>
                  )}
                </div>
                {(c.status === "processing" || c.status === "queued") && (
                  <Progress value={c.progress || 0} className="h-1 mt-2" />
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => { setSelected(c); setScreen("detail"); }} style={{ color: "var(--app-text-secondary)" }} data-testid={`view-${c.analysis_id}`}>Ver</Button>
                {c.status === "completed" && (
                  <Button size="sm" variant="ghost" onClick={() => handleDownload(c.analysis_id, c.campaign_name)} className="text-emerald-500 hover:bg-emerald-500/10" data-testid={`download-${c.analysis_id}`}>
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                {c.status !== "processing" && c.status !== "queued" && (
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(c)} className="text-rose-500 hover:bg-rose-500/10" data-testid={`delete-${c.analysis_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar campaña</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro querés eliminar <strong>{confirmDelete?.campaign_name}</strong>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-rose-600 hover:bg-rose-700 text-white">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
